import { Text, Box, Flex, useToast } from '@chakra-ui/react';
import { getIn } from 'formik';
import _ from 'lodash';
import React from 'react';

import { WipConfig } from '../../types';
import { AddIconButton } from 'packages/common-frontend/components/IconButton';
import { StickyHeaderSection } from 'packages/common-frontend/components/StickyHeaderSection';
import { errorToastAdminConfig } from 'packages/common-frontend/constants';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { FinancialCovenantTable } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/entity/FinancialCovenantEntity';

const createFinancialCovenantPropertyShell = (covenantKey: string) => ({
  name: 'Covenant',
  covenantKey,
  type: 'financialCovenantProperty',
  limit: {
    value: 0,
    valueType: 'currency',
    currency: 'USD',
  },
  limitType: 'min',
  value: _.startCase(covenantKey),
  _isWrittenIn_: true,
  _config_: {
    reportType: 'monthlyServicerReport',
    calculationType: 'xlsxParse',
    covenantValue: {
      sheet: 'Sheet',
      cell: 'A1',
    },
    reportingDate: {
      sheet: 'Sheet',
      cell: 'A1',
    },
  },
});

const addFinancialCovenant = ({
  wipConfig,
  basePropertyKey,
  toast,
}: {
  wipConfig: WipConfig;
  basePropertyKey: string;
  toast: Function;
}) => {
  const currentFinancialCovenants = getIn(
    wipConfig.formProps.values,
    basePropertyKey,
  );
  const currentFinancialCovenantKeys = _.map(
    currentFinancialCovenants,
    (currentFinancialCovenant) => currentFinancialCovenant.covenantKey,
  );

  const validCovenantKey = _.find(
    _.values(CreditAgreement.Config.FinancialCovenant.FinancialCovenantKey),
    (covenantKey) => !currentFinancialCovenantKeys.includes(covenantKey),
  );

  if (!validCovenantKey) {
    errorToastAdminConfig.title =
      'Could not find a valid financial covenant option.';
    errorToastAdminConfig.id = `${basePropertyKey}-add-error`;
    toast(errorToastAdminConfig);
    return;
  }

  const newFinancialCovenant = createFinancialCovenantPropertyShell(
    validCovenantKey,
  );

  wipConfig.formProps.setFieldValue(basePropertyKey, [
    newFinancialCovenant,
    ...currentFinancialCovenants,
  ]);
};

export const FinancialCovenants: React.FC<{
  financialCovenants: CreditAgreement.DomainEntity.FinancialCovenantProperty[];
  wipConfig?: WipConfig;
}> = ({ financialCovenants, wipConfig }) => {
  const toast = useToast();

  const basePropertyKey = 'financialCovenants';

  if (!wipConfig && !_.compact(Object.values(financialCovenants)).length) {
    return null;
  }

  return (
    <Box mt="40px" mb="35px">
      <StickyHeaderSection labelText="Financial Covenants">
        <Flex alignItems="center" mb="2">
          <Text fontWeight="bold" fontSize="20px">
            Properties
          </Text>
          {wipConfig?.isMutateAllowed(basePropertyKey) && (
            <>
              <AddIconButton
                ariaLabel="add financial covenant"
                label="Add Financial Covenant"
                onClick={() => {
                  addFinancialCovenant({
                    wipConfig,
                    basePropertyKey,
                    toast,
                  });
                }}
              />
            </>
          )}
        </Flex>

        <FinancialCovenantTable
          sanitizedFinancialCovenants={financialCovenants}
          basePropertyKey={basePropertyKey}
          wipConfig={wipConfig}
        />
      </StickyHeaderSection>
    </Box>
  );
};
